import { useCallback, useEffect, useRef, useState } from 'react';

import { useFocusEffect, useNavigation } from '@react-navigation/native';
import { NativeStackNavigationProp } from '@react-navigation/native-stack';

import WebView, { WebViewMessageEvent, WebViewProps } from '@/components/Webview';
import { useBackHandler } from '@/hooks/use-back-handler';
import { RootStackParamList } from '@/navigation/types';
import { isIos } from '@/utils/constants-platform-specific';
import { logger } from '@/utils/logging';

import { postMessageToRips } from '../utils/post-message-to-rips';
import { RipsHapticIntent, triggerRipsHaptic } from '../utils/rips-haptics';
import {
    RIPS_DEPOSIT_URL,
    RIPS_LOBBY_URL,
    expireRipsSession,
    hasRipsSessionFailed,
    refreshExpiredRipsSession,
} from './use-rips-session';

const LOG_TAG = '[useRipsWebViewBridge]';

const REPACKZ_MESSAGE_SOURCE = 'repackz';
const REPACKZ_PARENT_MESSAGE_SOURCE = 'repackz-parent';
const SESSION_EXPIRED_MESSAGE = 'session_expired';
const BALANCE_CHANGED_MESSAGE = 'balance_changed';
const NAVIGATE_BACK_MESSAGE = 'navigate_back';
const NAVIGATE_BACK_RESULT_MESSAGE = 'navigate_back_result';
const SESSION_READY_MESSAGE = 'session_ready';
const ENABLE_HAPTICS_MESSAGE = 'enable_haptics';
const HAPTIC_MESSAGE = 'haptic';
const NAVIGATE_LOBBY_MESSAGE = 'navigate_lobby';
const NAVIGATE_DEPOSIT_MESSAGE = 'navigate_deposit';
const ERROR_MESSAGE = 'error';
// No navigate_back_result within this time is treated as handled: false (game at root), per Repackz docs.
const NAVIGATE_BACK_TIMEOUT_MS = 300;
const SESSION_READY_TIMEOUT_MS = 15000;
const MAX_RELOAD_ATTEMPTS = 3;

type ShouldStartLoadRequest = Parameters<NonNullable<WebViewProps['onShouldStartLoadWithRequest']>>[0];

type RepackzMessage = {
    source?: string;
    type?: string;
    payload?: {
        handled?: boolean;
        intent?: RipsHapticIntent;
        message?: string;
        stack?: string;
    };
};

// Repackz is built for an iframe and posts window messages, so forward them to React Native.
const MESSAGE_BRIDGE_SCRIPT = /* js */ `window.addEventListener('message', function (event) {
    window.ReactNativeWebView.postMessage(JSON.stringify(event.data));
});
true;
`;

const BALANCE_CHANGED_PARENT_MESSAGE = {
    source: REPACKZ_PARENT_MESSAGE_SOURCE,
    type: BALANCE_CHANGED_MESSAGE,
};

const NAVIGATE_BACK_PARENT_MESSAGE = {
    source: REPACKZ_PARENT_MESSAGE_SOURCE,
    type: NAVIGATE_BACK_MESSAGE,
};

const ENABLE_HAPTICS_PARENT_MESSAGE = {
    source: REPACKZ_PARENT_MESSAGE_SOURCE,
    type: ENABLE_HAPTICS_MESSAGE,
};

const parseRepackzMessage = (data: unknown): RepackzMessage | null => {
    if (typeof data === 'object') {
        return data;
    }
    if (typeof data !== 'string') {
        return null;
    }
    try {
        return JSON.parse(data);
    } catch {
        return null;
    }
};

/**
 * WebView props that handle the Rips web app's navigation and messages.
 */
export const useRipsWebViewBridge = (url: string | undefined) => {
    const webViewRef = useRef<WebView>(null);
    const navigation = useNavigation<NativeStackNavigationProp<RootStackParamList>>();
    const navigateBackTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
    const [reloadAttempt, setReloadAttempt] = useState(0);
    const [showErrorOverlay, setShowErrorOverlay] = useState(false);
    const sessionReadyTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);

    const resolveNavigateBack = useCallback(
        (handled: boolean) => {
            if (!navigateBackTimerRef.current) {
                return;
            }
            clearTimeout(navigateBackTimerRef.current);
            navigateBackTimerRef.current = null;

            if (!handled && navigation.isFocused()) {
                // webview is at root, close the webview
                navigation.goBack();
            }
        },
        [navigation]
    );

    const onHardwareBackPress = useCallback(() => {
        if (!navigation.isFocused()) {
            return false;
        }
        if (navigateBackTimerRef.current) {
            return true;
        }
        navigateBackTimerRef.current = setTimeout(() => {
            resolveNavigateBack(false);
        }, NAVIGATE_BACK_TIMEOUT_MS);
        postMessageToRips(webViewRef.current, NAVIGATE_BACK_PARENT_MESSAGE);
        return true;
    }, [navigation, resolveNavigateBack, webViewRef]);

    useBackHandler(onHardwareBackPress);

    useEffect(() => {
        return () => {
            if (navigateBackTimerRef.current) {
                clearTimeout(navigateBackTimerRef.current);
            }
        };
    }, []);

    useEffect(() => {
        sessionReadyTimerRef.current = setTimeout(() => {
            logger.warn(LOG_TAG, `session_ready not received within ${SESSION_READY_TIMEOUT_MS}ms`);
            setShowErrorOverlay(true);
        }, SESSION_READY_TIMEOUT_MS);

        return () => {
            if (sessionReadyTimerRef.current) {
                clearTimeout(sessionReadyTimerRef.current);
            }
        };
    }, [reloadAttempt, url]);

    const clearSessionReadyTimer = useCallback(() => {
        if (sessionReadyTimerRef.current) {
            clearTimeout(sessionReadyTimerRef.current);
        }
    }, []);

    const refreshSession = useCallback(async () => {
        await refreshExpiredRipsSession();
        if (hasRipsSessionFailed()) {
            logger.warn(LOG_TAG, 'Failed to refresh the Rips session');
            clearSessionReadyTimer();
            setShowErrorOverlay(true);
            return false;
        }
        return true;
    }, [clearSessionReadyTimer]);

    const onSessionReady = useCallback(() => {
        clearSessionReadyTimer();
        setShowErrorOverlay(false);
    }, [clearSessionReadyTimer]);

    const refreshBalance = useCallback(() => {
        // used to make the webview refresh the balance after a deposit
        postMessageToRips(webViewRef.current, BALANCE_CHANGED_PARENT_MESSAGE);
    }, [webViewRef]);

    useFocusEffect(refreshBalance);

    const navigateToLobby = useCallback(() => {
        navigation.goBack();
    }, [navigation]);

    const navigateToDeposit = useCallback(() => {
        navigation.navigate('DepositStack', { screen: 'DepositScreen', params: {} });
    }, [navigation]);

    const confirmNavigationHandled = useCallback(
        (type: string) => {
            postMessageToRips(webViewRef.current, {
                source: REPACKZ_PARENT_MESSAGE_SOURCE,
                type: `${type}_result`,
                payload: { handled: true },
            });
        },
        [webViewRef]
    );

    // Handle the lobby and deposit URLs natively. Returning false stops the WebView from loading
    // them, which would show a blank page.
    const onShouldStartLoadWithRequest = useCallback(
        (request: ShouldStartLoadRequest) => {
            const { url: requestUrl } = request;

            if (requestUrl.startsWith(RIPS_LOBBY_URL)) {
                navigateToLobby();
                return false;
            }
            if (requestUrl.startsWith(RIPS_DEPOSIT_URL)) {
                navigateToDeposit();
                return false;
            }

            return true;
        },
        [navigateToLobby, navigateToDeposit]
    );

    const onMessage = useCallback(
        ({ nativeEvent }: WebViewMessageEvent) => {
            const message = parseRepackzMessage(nativeEvent.data);

            if (!message) {
                logger.debug(LOG_TAG, 'Unparseable message', nativeEvent.data);
                return;
            }

            if (message.source !== REPACKZ_MESSAGE_SOURCE) {
                return;
            }

            if (message.type === SESSION_EXPIRED_MESSAGE) {
                expireRipsSession();
                refreshSession();
            }
            if (message.type === NAVIGATE_BACK_RESULT_MESSAGE) {
                resolveNavigateBack(message.payload?.handled ?? false);
            }

            if (message.type === SESSION_READY_MESSAGE) {
                onSessionReady();
                if (isIos) {
                    // enabled haptics on ios only because on android Repackz already vibrates through the webview
                    postMessageToRips(webViewRef.current, ENABLE_HAPTICS_PARENT_MESSAGE);
                }
            }
            if (message.type === HAPTIC_MESSAGE) {
                triggerRipsHaptic(message.payload?.intent);
            }
            if (message.type === NAVIGATE_LOBBY_MESSAGE) {
                confirmNavigationHandled(message.type);
                navigateToLobby();
            }
            if (message.type === NAVIGATE_DEPOSIT_MESSAGE) {
                confirmNavigationHandled(message.type);
                navigateToDeposit();
            }
            if (message.type === ERROR_MESSAGE) {
                logger.error(LOG_TAG, 'Rips webview error message', {
                    message: message.payload?.message,
                    stack: message.payload?.stack,
                });
                clearSessionReadyTimer();
                setShowErrorOverlay(true);
            }
        },
        [
            resolveNavigateBack,
            onSessionReady,
            refreshSession,
            clearSessionReadyTimer,
            confirmNavigationHandled,
            navigateToLobby,
            navigateToDeposit,
        ]
    );

    const remountWebView = useCallback(() => {
        setShowErrorOverlay(false);
        setReloadAttempt(value => value + 1);
    }, []);

    const retryWebView = useCallback(async () => {
        if (await refreshSession()) {
            remountWebView();
        }
    }, [refreshSession, remountWebView]);

    const onWebviewProcessTerminated = useCallback(() => {
        if (reloadAttempt >= MAX_RELOAD_ATTEMPTS) {
            logger.error(LOG_TAG, 'WebView content process terminated, max attempts reached');
            clearSessionReadyTimer();
            setShowErrorOverlay(true);
            return;
        }
        logger.error(LOG_TAG, 'WebView content process terminated, remounting');
        // the webview must be remounted after its process gets terminated
        remountWebView();
    }, [reloadAttempt, remountWebView, clearSessionReadyTimer]);

    return {
        onMessage,
        onShouldStartLoadWithRequest,
        onWebviewProcessTerminated,
        injectedJavaScriptBeforeContentLoaded: MESSAGE_BRIDGE_SCRIPT,
        webViewRef,
        webViewKey: `rips-webview-${reloadAttempt}`,
        showErrorOverlay,
        retryWebView,
        navigateToLobby,
    };
};
